import { Popover, SelectMenu, Tabs, Toolbar } from '@box/blueprint-web';
import { FontColor } from '@box/blueprint-web-assets/icons/Fill';
import {
  Gray100,
  GrayWhite,
  Space1,
} from '@box/blueprint-web-assets/tokens/tokens';
import type { Editor } from '@tiptap/react';
import get from 'lodash/get';
import React from 'react';
import { useIntl } from 'react-intl';
import { highlightColors, textColors } from '../../../constants';
import messages, { type MessageKey } from '../../../messages';

type FontColorPickerProps = {
  editor: Editor;
};

const MENU_COLUMNS = 6;
const DEFAULT_TEXT_COLOR = Gray100;
const DEFAULT_HIGHLIGHT_COLOR = GrayWhite;

export const FontColorPicker = ({ editor }: FontColorPickerProps) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const intl = useIntl();

  const activeColor = get(
    editor.getAttributes('textStyle'),
    'color',
    DEFAULT_TEXT_COLOR,
  );
  const activeHighlight = get(
    editor.getAttributes('highlight'),
    'color',
    DEFAULT_HIGHLIGHT_COLOR,
  );

  return (
    <Popover.Root modal={false} onOpenChange={setIsOpen} open={isOpen}>
      <Popover.Trigger>
        <Toolbar.DropdownTriggerButton
          aria-label={intl.formatMessage(
            messages.editorToolbarColorPickerAriaLabel,
          )}
          selected={Boolean(activeHighlight)}
          backgroundColor={activeHighlight}
        >
          <Toolbar.Icon icon={FontColor} color={activeColor} />
          <Toolbar.DropdownIndicator direction={isOpen ? 'up' : 'down'} />
        </Toolbar.DropdownTriggerButton>
      </Popover.Trigger>
      <Popover.ContentContainer
        align="center"
        sideOffset={4}
        data-testid="color-popover-container"
      >
        <Tabs defaultSelectedId="text-color">
          <Tabs.TabList variant="contentSwitcher">
            <Tabs.Tab
              id="text-color"
              variant="contentSwitcher"
              data-testid="text-color-tab"
            >
              {intl.formatMessage(messages.tabTextColor)}
            </Tabs.Tab>
            <Tabs.Tab
              id="highlight-color"
              variant="contentSwitcher"
              data-testid="highlight-color-tab"
            >
              {intl.formatMessage(messages.tabHighlightColor)}
            </Tabs.Tab>
          </Tabs.TabList>
          <Tabs.TabPanel>
            <SelectMenu.Grid columns={MENU_COLUMNS}>
              {Object.entries(textColors).map(([key, textColor]) => (
                <SelectMenu.Option.ColorCircle
                  key={textColor}
                  active={textColor === activeColor}
                  aria-label={intl.formatMessage(messages[key as MessageKey])}
                  color={textColor}
                  onClick={() => {
                    editor.chain().focus().setColor(textColor).run();
                  }}
                  style={{ margin: Space1 }}
                />
              ))}
            </SelectMenu.Grid>
          </Tabs.TabPanel>
          <Tabs.TabPanel>
            <SelectMenu.Grid columns={MENU_COLUMNS}>
              {Object.entries(highlightColors).map(([key, highlightColor]) => (
                <SelectMenu.Option.ColorCircle
                  key={highlightColor}
                  active={highlightColor === activeHighlight}
                  aria-label={intl.formatMessage(messages[key as MessageKey])}
                  color={highlightColor}
                  onClick={() => {
                    editor
                      .chain()
                      .focus()
                      .toggleHighlight({ color: highlightColor })
                      .run();
                  }}
                  style={{ margin: Space1 }}
                />
              ))}
            </SelectMenu.Grid>
          </Tabs.TabPanel>
        </Tabs>
      </Popover.ContentContainer>
    </Popover.Root>
  );
};
